import type { AnchoringModel } from '@/shared/generated'

import { useTranslation } from 'react-i18next'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'

export const AnchoringRulesView = ({
	settings,
}: {
	settings: AnchoringModel
}) => {
	const { t } = useTranslation()

	return (
		<Card className='shadow-none'>
			<CardHeader>
				<CardTitle>
					{t('anchoring_rules', {
						defaultValue: 'Anchoring Rules',
					})}
				</CardTitle>
			</CardHeader>
			<CardContent className='flex flex-col gap-4 divide-y'>
				{settings.rules?.map((rule) => (
					<div key={rule.anchoring} className='flex w-full flex-col gap-4 pb-4'>
						<DescriptionList className='flex flex-col'>
							<DescriptionGroup className='flex flex-col'>
								<DescriptionTerm>
									{t('anchoring_type', {
										defaultValue: 'Anchor type',
									})}
								</DescriptionTerm>
								<DescriptionDetail className='font-semibold'>
									{t(`anchoring.${rule.anchoring}`, {
										defaultValue: rule.anchoring,
									})}
								</DescriptionDetail>
							</DescriptionGroup>
						</DescriptionList>
						<DescriptionList className='flex gap-4'>
							<DescriptionGroup className='flex flex-col'>
								<DescriptionTerm>
									{t('anchoring_low_range', {
										defaultValue: 'Low range',
									})}
								</DescriptionTerm>
								<DescriptionDetail className='font-semibold'>
									{rule.high_range} (
									{t(`behavior.${rule.high_range_behavior}`, {
										defaultValue: rule.high_range_behavior,
									})}
									)
								</DescriptionDetail>
							</DescriptionGroup>
							<DescriptionGroup className='flex flex-col'>
								<DescriptionTerm>
									{t('anchoring_high_range', {
										defaultValue: 'High range',
									})}
								</DescriptionTerm>
								<DescriptionDetail className='font-semibold'>
									{rule.low_range} (
									{t(`behavior.${rule.low_range_behavior}`, {
										defaultValue: rule.low_range_behavior,
									})}
									)
								</DescriptionDetail>
							</DescriptionGroup>
						</DescriptionList>
					</div>
				))}
			</CardContent>
			<CardHeader>
				<CardTitle>
					{t('anchoring.section.additional_settings.title', {
						defaultValue: 'Additional settings',
					})}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<DescriptionList className='flex flex-col'>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{t('anchoring.field.on_conflict.label', {
								defaultValue: 'On conflict',
							})}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{t(`anchoring.on_conflict.${settings?.on_conflict}`, {
								defaultValue: settings?.on_conflict,
							})}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{t('anchoring_on_anchor_locked', {
								defaultValue: 'On anchor locked',
							})}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{t(`anchoring.on_anchor_locked.${settings?.on_anchor_locked}`, {
								defaultValue: settings?.on_anchor_locked,
							})}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{t('anchoring_on_conflict_anchor_limits', {
								defaultValue: 'On conflict anchor limits',
							})}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{t(
								`anchoring.on_conflict_anchor_limits.${settings?.on_conflict_anchor_limits}`,
								{
									defaultValue: settings?.on_conflict_anchor_limits,
								}
							)}
						</DescriptionDetail>
					</DescriptionGroup>
					<DescriptionGroup className='flex flex-col'>
						<DescriptionTerm>
							{t('rounding_limits_and_price', {
								defaultValue: 'Rounding limits and price',
							})}
						</DescriptionTerm>
						<DescriptionDetail className='font-semibold'>
							{t(
								`anchoring.rounding_limits_and_price.${settings?.rounding_limits_and_price}`,
								{
									defaultValue: settings?.rounding_limits_and_price,
								}
							)}
						</DescriptionDetail>
					</DescriptionGroup>
				</DescriptionList>
			</CardContent>
		</Card>
	)
}
